import {makeAutoObservable} from 'mobx';
import {toast} from 'react-toastify';
import MicroreelsRepository from '../../../../data/repositories/MicroreelsRepository';
import LessonsRepository from '../../../../data/repositories/LessonsRepository';
import LessonContentRepository from '../../../../data/repositories/LessonContentRepository';
import {
  LessonContentMChoice,
  MULTIPLE_CHOICE,
} from '../../../../data/models/LessonContent/LessonContentBase';
import CoursesRepository from '../../../../data/repositories/CoursesRepository';
import FoldersRepository from '../../../../data/repositories/FoldersRepository';
import GradebookRepository from '../../../../data/repositories/GradebookRepository';
import LearnerRepository from '../../../../data/repositories/LearnerRepository';
import LibraryRepository from '../../../../data/repositories/LibraryRepository';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import {LearnerListItem} from '../../../../data/models/Learners/Learner';
import {MicroreelGroup} from '../../../../data/models/Microreels/MicroreelGroup';
import {Course, CourseListItem} from '../../../../data/models/Course/Course';
import {Folder} from '../../../../data/models/Course/Folder';
import {Lesson} from '../../../../data/models/Course/Lesson';
import LoadingModule from '../../../../modules/LoadingModule/LoadingModule';
import {getErrorFromCode, serverErrorCodes} from '../../../../data/errorCodes';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import {microreelsListText} from '../microreelsListText';

export const MICROREELS_PER_PAGE = 12;

class MicroreelsListStore {
  private microreelsRepo = new MicroreelsRepository();
  private lessonsRepo = new LessonsRepository();
  private coursesRepo = new CoursesRepository();
  private lessonContentRepo = new LessonContentRepository();

  private loadingModule = new LoadingModule();
  private editLoadingModule = new LoadingModule();
  private previewLoadingModule = new LoadingModule();
  private publishLoadingModule = new LoadingModule();
  private enrolledLoadingModule = new LoadingModule();
  private shareToCourseLoadingModule = new LoadingModule();
  private learnerRepo = new LearnerRepository();
  private libraryRepo = new LibraryRepository();
  private foldersRepo = new FoldersRepository();
  private gradebookRepo = new GradebookRepository();

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public get isEditLoading() {
    return this.editLoadingModule.isLoading;
  }

  public get isPreviewLoading() {
    return this.previewLoadingModule.isLoading;
  }

  public get isPublishing() {
    return this.publishLoadingModule.isLoading;
  }

  public get isEnrolledLoading() {
    return this.enrolledLoadingModule.isLoading;
  }

  public get isSharingToCourse() {
    return this.shareToCourseLoadingModule.isLoading;
  }

  public async getEnrolledLearners(
    organizationId: string,
    publishedCourseId: string,
    page: number,
    pageSize: number,
    search?: string,
  ) {
    this.enrolledLoadingModule.startLoading();
    const result = await this.learnerRepo.getMyLearners(
      page,
      organizationId,
      pageSize,
      [publishedCourseId],
      search,
    );
    this.enrolledLoadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    return {data: [] as LearnerListItem[], pagesCount: 0};
  }

  public async getEnrollmentCandidates(
    organizationId: string,
    publishedCourseId: string,
    page: number,
    pageSize: number,
    search?: string,
  ) {
    this.enrolledLoadingModule.startLoading();
    const result = await this.learnerRepo.getEnrollmentCandidates(
      organizationId,
      publishedCourseId,
      page,
      pageSize,
      search,
    );
    this.enrolledLoadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    return {data: [] as LearnerListItem[], pagesCount: 0};
  }

  public async enrollLearners(
    organizationId: string,
    publishedCourseId: string,
    learnerIds: string[],
  ) {
    this.enrolledLoadingModule.startLoading();
    const result = await this.learnerRepo.addLearnersToCourse(
      learnerIds,
      organizationId,
      publishedCourseId,
    );
    this.enrolledLoadingModule.endLoading();
    if (result.success) {
      toast.success(microreelsListText.enrollSuccess, toastConfig);
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async removeLearnersFromGroup(
    organizationId: string,
    publishedCourseId: string,
    learnerIds: string[],
  ) {
    this.enrolledLoadingModule.startLoading();
    const results = await Promise.all(
      learnerIds.map(learnerId =>
        this.learnerRepo.removeLearnerFromTheCourse(
          organizationId,
          learnerId,
          publishedCourseId,
        ),
      ),
    );
    this.enrolledLoadingModule.endLoading();
    const failed = results.filter(result => !result.success);
    if (failed.length) {
      const firstError = failed[0].errors?.[0];
      toast.error(
        firstError
          ? getErrorFromCode(firstError)
          : microreelsListText.removeLearnersError,
        toastConfig,
      );
    } else {
      toast.success(microreelsListText.removeLearnersSuccess, toastConfig);
    }
    return failed.length === 0;
  }

  public async getPublishedGroup(organizationId: string, groupId: string) {
    const result = await this.coursesRepo.getPublishedCourseVersion(
      organizationId,
      groupId,
    );
    if (result.success && result.data) {
      return result.data;
    }
    return undefined;
  }

  // Republish without the success toast: keeps the published copy in sync
  // after title/description edits, so the copy-to-course library and the
  // learner feed see the change (they read the PublishedLesson).
  public async republishGroupSilent(
    organizationId: string,
    groupId: string,
    options?: {ignoreNotPublished?: boolean},
  ) {
    const result = await this.coursesRepo.republishCourse(
      organizationId,
      groupId,
    );
    if (!result.success && result.errors && result.errors.length) {
      // A draft group has no published copy to sync: the backend answers
      // entity_not_found. Opportunistic callers treat that as "nothing to
      // do" instead of surfacing an error.
      if (
        options?.ignoreNotPublished &&
        result.errors[0] === serverErrorCodes.ENTITY_NOT_FOUND
      ) {
        return true;
      }
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
      return result.success;
    }
    return result.success;
  }

  // Last-chance republish fired while the page unloads (keepalive fetch):
  // the regular axios call would be aborted with the tab.
  public republishGroupKeepalive(organizationId: string, groupId: string) {
    this.coursesRepo.republishCourseKeepalive(organizationId, groupId);
  }

  public async publishGroup(
    organizationId: string,
    groupId: string,
    alreadyPublished: boolean,
  ) {
    if (this.publishLoadingModule.isLoading) return false;
    this.publishLoadingModule.startLoading();
    const result = alreadyPublished
      ? await this.coursesRepo.republishCourse(organizationId, groupId)
      : await this.coursesRepo.publishCourse(organizationId, groupId);
    this.publishLoadingModule.endLoading();
    if (result.success) {
      toast.success(
        alreadyPublished
          ? microreelsListText.republishSuccess
          : microreelsListText.publishSuccess,
        toastConfig,
      );
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async getMicroreels(
    organizationId: string,
    page: number,
    search?: string,
  ) {
    this.loadingModule.startLoading();
    const result = await this.microreelsRepo.getMicroreelsList(
      organizationId,
      page,
      MICROREELS_PER_PAGE,
      search,
    );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    return {
      pagesCount: 0,
      data: [] as MicroreelGroup[],
    };
  }

  public async getReel(organizationId: string, reelId: string) {
    this.editLoadingModule.startLoading();
    const result = await this.lessonsRepo.getLessonById(organizationId, reelId);
    this.editLoadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return undefined;
  }

  public async getReelPreview(
    organizationId: string,
    groupId: string,
    reelId: string,
  ) {
    this.previewLoadingModule.startLoading();
    const result = await this.lessonsRepo.getLessonPreview(
      organizationId,
      groupId,
      reelId,
    );
    this.previewLoadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return undefined;
  }

  public async getReelQuestion(organizationId: string, reelId: string) {
    this.editLoadingModule.startLoading();
    const result = await this.lessonContentRepo.getLessonContent(
      organizationId,
      reelId,
    );
    this.editLoadingModule.endLoading();
    if (result.success && result.data) {
      const items = result.data.contentList?.items ?? {};
      return Object.values(items)
        .map(item => item.data)
        .find(data => data.ivNodeType === MULTIPLE_CHOICE) as
        | LessonContentMChoice
        | undefined;
    }
    if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return undefined;
  }

  public async saveReelQuestion(
    organizationId: string,
    reelId: string,
    node: LessonContentMChoice,
  ) {
    this.editLoadingModule.startLoading();
    const result = await this.lessonContentRepo.editLessonContent(
      organizationId,
      reelId,
      node,
    );
    this.editLoadingModule.endLoading();
    if (result.success) {
      toast.success(microreelsListText.questionSaved, toastConfig);
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  // Background fetch of the editor chunks of a source video: used to build
  // the persistent chunk→reel map before opening the video editor.
  public async getVideoChunksSilent(
    organizationId: string,
    mediaFileId: string,
  ) {
    const result = await this.libraryRepo.getVideoChunksForGeneratedContent(
      organizationId,
      mediaFileId,
    );
    if (result.success && result.data) {
      return result.data.chunks;
    }
    return undefined;
  }

  // Background fetch of a library file (no spinner, no toasts): the group
  // view uses it to know whether a reel's source video is AI-generated and
  // therefore editable in the video editor.
  public async getMediaFileSilent(
    organizationId: string,
    mediaFileId: string,
  ): Promise<MediaFile | undefined> {
    const result = await this.libraryRepo.getByContentId(
      organizationId,
      mediaFileId,
    );
    if (result.success && result.data) {
      return result.data;
    }
    return undefined;
  }

  // The folder lessons endpoint returns the raw Lesson entity, which exposes
  // fields the microreels list projection lacks: the acknowledgment flag and
  // the generation language/dialect/gender.
  public async getFolderLessonsSilent(
    organizationId: string,
    folderId: string,
  ) {
    const result = await this.lessonsRepo.getLessons(organizationId, folderId);
    return result.success && result.data ? result.data : [];
  }

  // Background fetch for reel metadata (duration/questions): no spinner, no
  // toasts, and it uses the lesson content endpoint (lighter and available
  // even when the preview endpoint fails).
  public async getReelContentSilent(organizationId: string, reelId: string) {
    const result = await this.lessonContentRepo.getLessonContent(
      organizationId,
      reelId,
    );
    if (result.success && result.data) {
      return result.data;
    }
    return undefined;
  }

  public async getReelPreviewSilent(
    organizationId: string,
    groupId: string,
    reelId: string,
  ) {
    const result = await this.lessonsRepo.getLessonPreview(
      organizationId,
      groupId,
      reelId,
    );
    if (result.success && result.data) {
      return result.data;
    }
    return undefined;
  }

  public async editReel(organizationId: string, lesson: Lesson) {
    this.editLoadingModule.startLoading();
    const result = await this.lessonsRepo.editLesson(organizationId, lesson);
    this.editLoadingModule.endLoading();
    if (result.success) {
      toast.success(microreelsListText.editInfoSuccess, toastConfig);
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async reorderReels(
    organizationId: string,
    folderId: string,
    lessons: Array<{id: string; order: number}>,
  ) {
    // The backend only reads Id and Order from each lesson on this endpoint.
    const result = await this.lessonsRepo.orderLessons(
      organizationId,
      folderId,
      lessons as unknown as Lesson[],
    );
    if (result.success) {
      toast.success(microreelsListText.orderSaved, toastConfig);
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async deleteReel(organizationId: string, reelId: string) {
    const result = await this.lessonsRepo.deleteLesson(organizationId, reelId);
    if (result.success) {
      toast.success(microreelsListText.deleteReelSuccess, toastConfig);
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async getGroup(organizationId: string, groupId: string) {
    this.editLoadingModule.startLoading();
    const result = await this.coursesRepo.getCourse(organizationId, groupId);
    this.editLoadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return undefined;
  }

  public async editGroup(organizationId: string, course: Course, cover?: File) {
    this.editLoadingModule.startLoading();
    const result = await this.coursesRepo.editCourse(organizationId, course);
    if (result.success && cover && course.id) {
      await this.coursesRepo.editCoursePoster(organizationId, course.id, cover);
    }
    this.editLoadingModule.endLoading();
    if (result.success) {
      toast.success(microreelsListText.editInfoSuccess, toastConfig);
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async cancelGeneration(organizationId: string) {
    const result = await this.microreelsRepo.cancelGeneration(organizationId);
    if (result.success) {
      toast.success(microreelsListText.generationCanceled, toastConfig);
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async deleteGroup(organizationId: string, groupId: string) {
    const result = await this.coursesRepo.deleteCourse(organizationId, groupId);
    if (result.success) {
      toast.success(microreelsListText.deleteGroupSuccess, toastConfig);
    } else if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  // The org course list already leaves out microreel groups and posts.
  public async getShareableCourses(
    organizationId: string,
    page: number,
    pageSize: number,
    search?: string,
  ) {
    const result = await this.coursesRepo.getOrganizationCourses(
      organizationId,
      page,
      pageSize,
      search,
    );
    if (result.success && result.data) {
      return result.data;
    }
    return {data: [] as CourseListItem[], pagesCount: 0};
  }

  public async getCourseSections(organizationId: string, courseId: string) {
    const result = await this.foldersRepo.getCourseFolders(
      organizationId,
      courseId,
    );
    if (result.success && result.data) {
      return [...result.data].sort((x, y) => x.order - y.order);
    }
    this.toastShareError(result.errors);
    return [] as Folder[];
  }

  // Copies the published reels of a group into a course section, in feed
  // order. copy_to_course takes published lesson ids, and the grade book is
  // the endpoint that lists them for a published course. A course without
  // sections gets one named newSectionName first.
  public async shareGroupToCourse(
    organizationId: string,
    publishedCourseId: string,
    courseId: string,
    section: {folderId?: string; newSectionName: string},
  ) {
    if (this.shareToCourseLoadingModule.isLoading) return undefined;
    this.shareToCourseLoadingModule.startLoading();
    try {
      const gradebook = await this.gradebookRepo.getCourseGradebook(
        organizationId,
        publishedCourseId,
      );
      if (!gradebook.success || !gradebook.data) {
        this.toastShareError(gradebook.errors);
        return undefined;
      }
      const reelIds = [...gradebook.data.lessons]
        .sort(
          (x, y) =>
            (x.folderOrder ?? 0) - (y.folderOrder ?? 0) || x.order - y.order,
        )
        .map(lesson => lesson.lessonId);
      if (!reelIds.length) {
        toast.error(microreelsListText.shareToCourseNothing, toastConfig);
        return undefined;
      }
      let folderId = section.folderId;
      if (!folderId) {
        const created = await this.foldersRepo.createFolder(organizationId, {
          name: section.newSectionName,
          order: 0,
          courseId,
        });
        if (!created.success || !created.data?.id) {
          this.toastShareError(created.errors);
          return undefined;
        }
        folderId = created.data.id as string;
      }
      const copied = await this.microreelsRepo.copyMicroreelsToCourse(
        organizationId,
        courseId,
        folderId,
        reelIds,
      );
      if (!copied.success) {
        this.toastShareError(copied.errors);
        return undefined;
      }
      return {folderId, count: reelIds.length};
    } finally {
      this.shareToCourseLoadingModule.endLoading();
    }
  }

  private toastShareError(errors?: string[]) {
    toast.error(
      errors && errors.length
        ? getErrorFromCode(errors[0])
        : microreelsListText.shareToCourseError,
      toastConfig,
    );
  }
}

export default MicroreelsListStore;
